import { useInfiniteQuery } from '@tanstack/react-query';

import { TableFilterState } from '@/components/data-table/filters-v2';
import { DEFAULT_PAGE_SIZE, getNextPageParam } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { TimeOffRequest } from '@/modules/time-off/types';

import { getTimeOffRequests } from '../api/time-offs.api';
import { getMemberTimeOffRequestsQueryKey } from '../constants/member-time-offs-query-keys';
import { mapTimeOffFilters } from '../mappers/time-off-filters.mapper';

const MEMBER_TIME_OFF_REQUESTS_STALE_TIME = 5 * 60 * 1000;

const buildMemberTimeOffRequestsFilter = ({
  filters,
  memberId,
}: {
  filters: TableFilterState;
  memberId: string;
}) => {
  const requesterId = Number(memberId);
  const mappedFilters = mapTimeOffFilters(filters);

  if (Number.isNaN(requesterId)) {
    return mappedFilters;
  }

  return { ...mappedFilters, requesterIds: [requesterId] };
};

/**
 * Infinite query of a single team member's time-off requests. Mirrors
 * `useTimeOffRequests` but keyed by memberId.
 */
export const useMemberTimeOffRequests = (
  memberId: string | undefined,
  filters: TableFilterState
) => {
  return useInfiniteQuery({
    queryKey: getMemberTimeOffRequestsQueryKey(memberId ?? '', filters),
    queryFn: ({ pageParam }) =>
      getTimeOffRequests({
        filter: buildMemberTimeOffRequestsFilter({ filters, memberId: memberId as string }),
        pagination: { cursor: pageParam, pageSize: DEFAULT_PAGE_SIZE },
      }),
    initialPageParam: null as number | null,
    getNextPageParam,
    enabled: !!memberId,
    staleTime: MEMBER_TIME_OFF_REQUESTS_STALE_TIME,
    select: data =>
      new PaginatedResponse<TimeOffRequest>({
        items: data.pages.flatMap(page => page.items),
        pagination: data.pages[data.pages.length - 1]?.pagination ?? { total: 0, cursor: null },
      }),
  });
};
